/**
 * Gezondheid > Koppelingen (sessie 125).
 *
 * Per bron (Picqer, Lightspeed, ...) de uitkomst van de leescontrole die
 * elke 5 minuten draait: status, laatste contact, responstijd en bij een
 * fout de oorzaak. Alleen voor de beheerder (de API is ook alleen voor
 * beheerders).
 *
 * JP is kleurenblind: elke status staat er als woord plus symbool, de
 * kleur (blauw, paars, oranje, grijs uit gedeeld/kleuren.css) is alleen extra.
 * Nooit groen tegenover rood.
 *
 * Sessie 141 B1: het woord voor een uitgevallen bron is "storing", en
 * onder de bronnen staan de achtergrondtaken (nachtrondes en wachtrijen):
 * stand, wanneer laatst goed, en een telling met een let-op als een taak
 * wel draait maar niets doet. Eigen leesweg, zodat een stukke telling de
 * koppelingen laat staan.
 */
import { useQuery } from "@tanstack/react-query";

import { useBeheerder } from "../../WerkplekV6/gedeeld/rol";
import { Bijgewerkt, LaadStand } from "../../WerkplekV6/gedeeld/onderdelen";
import "../../WerkplekV6/gedeeld/gedeeld.css";

import {
  type Achtergrondtaak,
  drempelTekst,
  koppelingenApi,
  laatsteContact,
  samenvattingTekst,
  taakWeergave,
  type KoppelingBron,
  tijdstip,
  weergave,
} from "./koppelingenApi";
import "./koppelingen.css";

/** Hoe vaak het blok opnieuw vraagt (de controle zelf draait elke 5 min). */
export const VERVERS_MS = 60_000;

function StandWoord({ w }: { w: { woord: string; symbool: string; kleur: string } }) {
  return (
    <span
      className={`m61-stand kp-stand ${w.kleur}`}
      data-testid="kp-status"
      data-status={w.woord}
    >
      <span className="kp-symbool" aria-hidden="true">
        {w.symbool}
      </span>
      {w.woord}
    </span>
  );
}

function StatusWoord({ status }: { status: string }) {
  return <StandWoord w={weergave(status)} />;
}

function TaakRij({ taak }: { taak: Achtergrondtaak }) {
  const storing = taak.stand === "storing";
  return (
    <li className="m61-rij kp-rij" data-testid={`kp-taak-${taak.sleutel}`}>
      <div className="l">
        <div className="n">{taak.naam}</div>
        <div className="s kp-feiten">
          <span>
            Laatst goed:{" "}
            {taak.laatste_goed_op
              ? laatsteContact(taak.laatste_goed_op)
              : taak.laatste_fout_op
                ? "nog nooit geslaagd"
                : "nog niet gedraaid"}
          </span>
          {taak.telling ? <span data-testid="kp-telling">{taak.telling}</span> : null}
        </div>
        {storing && taak.laatste_fout_op ? (
          <div className="kp-oorzaak" data-testid="kp-oorzaak">
            Laatste run mislukt om {tijdstip(taak.laatste_fout_op)}
          </div>
        ) : null}
        {taak.let_op ? (
          <div className="kp-oorzaak" data-testid="kp-let-op">
            Let op: {taak.let_op}
          </div>
        ) : null}
        <div className="kp-toelichting">{taak.wat}</div>
      </div>
      <div className="r">
        <StandWoord w={taakWeergave(taak.stand)} />
      </div>
    </li>
  );
}

function Achtergrondtaken() {
  const vraag = useQuery({
    queryKey: ["bewaking", "koppelingen", "taken"],
    queryFn: koppelingenApi.haalTaken,
    refetchInterval: VERVERS_MS,
  });
  const taken = vraag.data?.taken ?? [];
  return (
    <div className="kp-taken" data-testid="kp-taken">
      <h3 className="kp-kop">Achtergrondtaken</h3>
      {vraag.isError || vraag.isLoading ? (
        <LaadStand
          laadt={vraag.isLoading}
          fout={vraag.error}
          watFout="De achtergrondtaken zijn niet op te halen."
          opnieuw={() => void vraag.refetch()}
        />
      ) : taken.length === 0 ? (
        <p className="m61-leeg" data-testid="kp-taken-leeg">
          Nog geen gegevens over de achtergrondtaken.
        </p>
      ) : (
        <ul className="kp-lijst">
          {taken.map((t) => (
            <TaakRij key={t.sleutel} taak={t} />
          ))}
        </ul>
      )}
    </div>
  );
}

function BronRij({ bron }: { bron: KoppelingBron }) {
  const uitgevallen = bron.status === "uitgevallen";
  // Eén mislukte controle maakt nog geen uitval (twee op rij), maar JP
  // ziet de oorzaak wel alvast.
  const losseFout = !uitgevallen && bron.mislukt_op_rij > 0 && !!bron.laatste_fout_oorzaak;
  return (
    <li className="m61-rij kp-rij" data-testid={`kp-bron-${bron.bron}`}>
      <div className="l">
        <div className="n">{bron.naam}</div>
        <div className="s kp-feiten">
          <span>Laatste contact: {laatsteContact(bron.laatste_succes_op)}</span>
          {bron.responstijd_ms !== null && bron.responstijd_ms !== undefined ? (
            <span className="num">{Math.round(bron.responstijd_ms)} ms</span>
          ) : null}
          {bron.status === "traag" && typeof bron.traag_drempel_ms === "number" ? (
            <span className="num" data-testid="kp-drempel">
              {drempelTekst(bron.traag_drempel_ms)}
            </span>
          ) : null}
        </div>
        {uitgevallen ? (
          <div className="kp-oorzaak" data-testid="kp-oorzaak">
            {bron.laatste_fout_oorzaak ?? "Oorzaak onbekend"}
            {bron.uitgevallen_sinds ? `, sinds ${tijdstip(bron.uitgevallen_sinds)}` : ""}
          </div>
        ) : losseFout ? (
          <div className="kp-oorzaak los" data-testid="kp-oorzaak">
            Laatste controle mislukt
            {bron.laatste_fout_op ? ` om ${tijdstip(bron.laatste_fout_op)}` : ""}:{" "}
            {bron.laatste_fout_oorzaak}
          </div>
        ) : bron.status === "onbekend" && bron.laatste_fout_oorzaak ? (
          <div className="kp-oorzaak los" data-testid="kp-oorzaak">
            {bron.laatste_fout_oorzaak}
          </div>
        ) : null}
        {bron.toelichting ? <div className="kp-toelichting">{bron.toelichting}</div> : null}
      </div>
      <div className="r">
        <StatusWoord status={bron.status} />
      </div>
    </li>
  );
}

/** Het blok, alleen voor de beheerder; anders niets en geen verzoek. */
export function KoppelingenBlok() {
  const beheerder = useBeheerder();
  return beheerder ? <KoppelingenVoorBeheerder /> : null;
}

function KoppelingenVoorBeheerder() {
  const vraag = useQuery({
    queryKey: ["bewaking", "koppelingen"],
    queryFn: koppelingenApi.haal,
    refetchInterval: VERVERS_MS,
  });
  const k = vraag.data;
  const bronnen = k?.bronnen ?? [];
  return (
    <section
      className="m61-vlak kp-blok"
      aria-label="Koppelingen"
      data-testid="kp-blok"
    >
      <div className="m61-vk">
        <h2>Koppelingen</h2>
        <span className="r">
          <Bijgewerkt moment={k?.bijgewerkt_op} />
        </span>
      </div>
      <div className="m61-vi">
        {vraag.isError || vraag.isLoading ? (
          <LaadStand
            laadt={vraag.isLoading}
            fout={vraag.error}
            watFout="De koppelingen zijn niet op te halen."
            opnieuw={() => void vraag.refetch()}
          />
        ) : bronnen.length === 0 ? (
          <p className="m61-leeg" data-testid="kp-leeg">
            Nog geen controle gedraaid. Dit vult zodra de koppelingscontrole
            (elke 5 minuten) voor het eerst gelopen heeft.
          </p>
        ) : (
          <>
            {k?.samenvatting ? (
              <p className="kp-samenvatting" data-testid="kp-samenvatting">
                {samenvattingTekst(k.samenvatting)}
              </p>
            ) : null}
            <ul className="kp-lijst">
              {bronnen.map((b) => (
                <BronRij key={b.bron} bron={b} />
              ))}
            </ul>
          </>
        )}
        <Achtergrondtaken />
      </div>
    </section>
  );
}
